import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import { useAuth } from '../../context/AuthContext';
import { isRoad } from '../../utils/partnerTypes';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import { Avatar, Badge, Button, Modal } from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';
import { APP } from '../../constants';
import { colors } from '../../theme';

const MENU = [
  { section: 'My activity' },
  { icon: '📅', label: 'My bookings', to: '/activity?tab=bookings' },
  { icon: '🧾', label: 'My orders', to: '/activity?tab=orders' },
  { icon: '🧑‍🔧', label: 'Job seeker profile', to: '/work-profile' },
  // Hidden from ride, cab and parcel partners - they do not hire.
  { icon: '🔎', label: 'Job seekers', to: '/people', hiringOnly: true },
  { icon: '💼', label: 'My job applications', to: '/my-applications' },
  { icon: '🚗', label: 'My vehicle listings', to: '/my-vehicles' },
  { icon: '🏠', label: 'Property & animals', to: '/property' },
  { icon: '🏨', label: 'My stays', to: '/my-stays' },
  { icon: '🏍️', label: 'My vehicles & service', to: '/my-vehicles-service' },
  { icon: '🎂', label: 'Special days', to: '/special-days' },
  { icon: '🎧', label: 'Help & support', to: '/support' },
  { icon: '✨', label: 'My custom requests', to: '/desire' },

  { section: 'Account' },
  { icon: '📍', label: 'Saved addresses', to: '/addresses' },
  { icon: '🎨', label: 'Appearance & theme', to: '/profile/appearance' },
  { icon: '🔔', label: 'Notifications', to: '/notifications' },
  { icon: '⭐', label: 'My reviews', to: '/my-reviews' },

  { section: 'KAAMWALA' },
  { icon: '🏪', label: 'Sell on KAAMWALA', to: '/sell', highlight: true },
  { icon: '📞', label: 'Help & support', action: 'support' },
  { icon: 'ℹ️', label: 'About KAAMWALA', action: 'about' },
];

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, partner, isPartner, hasBusiness, setMode, signOut } = useAuth();
  const menu = MENU.filter((m) => !m.hiringOnly || !isRoad(partner));
  const { location } = useApp();
  const toast = useToast();
  const [stats, setStats] = useState(null);
  const [about, setAbout] = useState(false);

  useEffect(() => {
    api.user.dashboard().then((res) => setStats(res.data)).catch(() => setStats(null));
  }, []);

  const handle = (item) => {
    if (item.action === 'support') { window.location.href = `tel:${APP.supportPhone}`; return; }
    if (item.action === 'about') { setAbout(true); return; }
    navigate(item.to);
  };

  const confirmSignOut = async () => {
    const ok = await toast.confirm({
      title: 'Log out',
      message: 'Are you sure you want to log out?',
      confirmLabel: 'Log out',
      danger: true,
    });
    if (ok) { await signOut(); navigate('/'); }
  };

  return (
    <div>
      {/* Profile header */}
      <Reveal
        style={{
          background: colors.primary, borderRadius: 22, padding: 22, color: '#fff',
        }}
      >
        <div className="row" style={{ gap: 16 }}>
          <Avatar
            name={user && user.name}
            uri={user && user.avatar}
            size={64}
            style={{ background: 'rgba(255,255,255,.2)', color: '#fff' }}
          />
          <div className="grow">
            <div style={{ fontSize: 20, fontWeight: 800 }}>{(user && user.name) || 'Add your name'}</div>
            <div style={{ opacity: 0.85, fontSize: 13, marginTop: 2 }}>+91 {user && user.phone}</div>
            {isPartner ? (
              <div style={{ marginTop: 6 }}>
                <Badge value="Partner account" color="#fff" bg="rgba(255,255,255,.2)" />
              </div>
            ) : null}
          </div>
          <Button title="Edit" icon="✏️" variant="light" size="sm" onClick={() => navigate('/profile/edit')} />
        </div>

        {stats ? (
          <div
            className="grid"
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
              gap: 10, marginTop: 20,
            }}
          >
            {[
              { label: 'Bookings', value: stats.bookings },
              { label: 'Orders', value: stats.orders },
              { label: 'Applications', value: stats.applications },
              { label: 'Listings', value: stats.listings },
            ].map((s, i) => (
              <Reveal
                key={s.label}
                delay={i * 60}
                style={{
                  background: 'rgba(255,255,255,.14)', borderRadius: 14,
                  padding: '12px 8px', textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 20, fontWeight: 800 }}>{s.value || 0}</div>
                <div style={{ fontSize: 11, opacity: 0.85 }}>{s.label}</div>
              </Reveal>
            ))}
          </div>
        ) : null}
      </Reveal>

      {/* Location */}
      <div className="card sh-sm row" style={{ marginTop: 16, gap: 12 }}>
        <span style={{ fontSize: 18 }}>📍</span>
        <div className="grow">
          <div className="t-tiny">CURRENT LOCATION</div>
          <div className="t-title">{location.label || location.city}</div>
          {location.address ? <div className="t-tiny clamp-2">{location.address}</div> : null}
        </div>
        <Link to="/location" className="link-btn">Change</Link>
      </div>

      {isPartner && hasBusiness ? (
        <Button
          title="Switch to partner dashboard"
          icon="🏪"
          variant="ghost"
          full
          style={{ marginTop: 12 }}
          onClick={() => { setMode('partner'); navigate('/partner'); }}
        />
      ) : null}

      {/* Menu */}
      <div style={{ marginTop: 20 }}>
        {menu.map((item, i) => (item.section ? (
          <div
            key={item.section}
            className="t-tiny"
            style={{ letterSpacing: 1, margin: '22px 0 8px' }}
          >
            {item.section.toUpperCase()}
          </div>
        ) : (
          <Reveal key={item.label} delay={stagger(i, 20)}>
            <button
              type="button"
              onClick={() => handle(item)}
              className="press"
              style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
                background: item.highlight ? colors.accentLight : '#fff',
                border: `1px solid ${item.highlight ? '#F8D2AE' : colors.line}`,
                borderRadius: 14, padding: 12, marginBottom: 8,
              }}
            >
              <span
                style={{
                  width: 36, height: 36, borderRadius: 10, flex: '0 0 auto',
                  background: item.highlight ? '#fff' : colors.bg,
                  display: 'grid', placeItems: 'center', fontSize: 16,
                }}
              >
                {item.icon}
              </span>
              <span className="t-body grow" style={{ fontWeight: 600, color: colors.ink }}>{item.label}</span>
              <span style={{ fontSize: 18, color: colors.faint }}>›</span>
            </button>
          </Reveal>
        )))}
      </div>

      <Button title="Log out" variant="danger" full style={{ marginTop: 20 }} onClick={confirmSignOut} />

      <div className="t-tiny" style={{ textAlign: 'center', marginTop: 20, lineHeight: 1.7 }}>
        {APP.name} · {APP.tagline}
        <br />
        Version 1.0.0
      </div>

      <Modal open={about} onClose={() => setAbout(false)} title="About KAAMWALA">
        <p className="t-body" style={{ lineHeight: 1.7 }}>
          {APP.tagline}
        </p>
        <p className="t-body" style={{ lineHeight: 1.7, marginTop: 12 }}>
          KAAMWALA connects you with local shops, service providers, professionals, drivers and
          employers — all in one account.
        </p>
        <p className="t-small" style={{ marginTop: 12 }}>
          Support: <a href={`tel:${APP.supportPhone}`} style={{ color: colors.primary, fontWeight: 700 }}>{APP.supportPhone}</a>
        </p>
      </Modal>
    </div>
  );
};

export default ProfilePage;
